import { DateTime, Duration } from "luxon";
import { HipTask } from "@opstel/query/models/hipTask";
import { formatDuration } from "@shared/common/utils/luxonHelpers";
import { useEffect, useState } from "react";

type HipTaskStatusTagProps = Pick<
  HipTask,
  "status" | "maximumComputationTime" | "taskStartedAt"
> & {
  readonly showInfoIcon: boolean;
  readonly queuePosition?: number;
};

export function HipTaskStatusTag({
  status,
  queuePosition,
  maximumComputationTime,
  taskStartedAt,
  showInfoIcon,
}: HipTaskStatusTagProps) {
  const { color, tagMode } = hipTaskStatusToColor[status];
  const [text, setText] = useState<string | undefined>();

  useEffect(() => {
    // Update the text field with an interval to make sure the count down re-renders.
    if (status === "Running" && taskStartedAt && maximumComputationTime) {
      const interval = setInterval(() => {
        setText(getRemainingTimeText(taskStartedAt, maximumComputationTime));
      }, 1000);

      return () => clearInterval(interval);
    } else if (status === "InQueue" && queuePosition) {
      setText(` (${queuePosition})`);
    } else {
      setText(undefined);
    }
  }, [status, taskStartedAt, maximumComputationTime, queuePosition, setText]);

  return (
    <tractie-tag
      color={color}
      appearance={tagMode === "subtle" ? "subtle" : undefined}
      size="large"
    >
      <div className="flex items-center gap-s">
        {showInfoIcon && <tractie-icon name="info" size="xs" />}
        {status === "Running" && <tractie-loader className="size-4" size="xs" />}
        <span className="text-center text-s leading-l font-normal whitespace-nowrap">
          {hipTaskStatusToText[status]}
          {text}
        </span>
      </div>
    </tractie-tag>
  );
}

const hipTaskStatusToColor = {
  Unknown: { color: "gray", tagMode: "default" },
  InQueue: { color: "yellow", tagMode: "subtle" },
  Running: { color: "yellow", tagMode: "default" },
  Completed: { color: "green", tagMode: "default" },
  FailedWithResult: { color: "red", tagMode: "default" },
  Failed: { color: "red", tagMode: "default" },
  SentToDonna: { color: "gray", tagMode: "default" },
} as const;

const hipTaskStatusToText = {
  Unknown: "Onbekend",
  InQueue: "Wachtrij",
  Running: "Bezig",
  Completed: "Opgelost",
  FailedWithResult: "Fout in oplossing",
  Failed: "Mislukt",
  SentToDonna: "Afgerond",
} as const;

function getRemainingTimeText(taskStartedAt: DateTime, maximumComputationTime: Duration) {
  const now = DateTime.local();
  const duration = calculateRemainingTime(now, taskStartedAt, maximumComputationTime);
  const formatString = maximumComputationTime.as("seconds") <= 59 ? "%s" : "%d %h%M";

  if (duration.as("seconds") <= 0) {
    return "";
  }

  return ` (max. ${formatDuration(duration, formatString)})`;
}

function calculateRemainingTime(
  currentTime: DateTime,
  taskStartedAt: DateTime,
  maximumComputationTime: Duration,
): Duration {
  const timePassed = currentTime.diff(taskStartedAt);
  return maximumComputationTime.minus(timePassed);
}
